import { Injectable, NgZone } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { BehaviorSubject } from 'rxjs';
import { UserNotification } from '../interfaces/userNotification';
import * as firebase from 'firebase/compat/app';
import { NotificationService } from './notification.service';
import { Notification as AppNotification } from '../interfaces/notification';
import { StorageTypeKey } from '../enums/StorageTypeKey';
import { environment } from '../../../environments/environment';

@Injectable()
export class FirebaseService {
  observables = new Map<string, BehaviorSubject<UserNotification>>();

  getObservable(topic: string): BehaviorSubject<UserNotification> {
    return this.observables.get(topic);
  }

  subscribe(topic: string, observer: any): BehaviorSubject<UserNotification> {
    let behaviorSubject = this.observables.get(topic);
    if (!behaviorSubject) {
      behaviorSubject = new BehaviorSubject<UserNotification>(null);
    }
    behaviorSubject.subscribe(observer);
    this.observables.set(topic, behaviorSubject);
    return this.observables.get(topic);
  }

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private notificationService: NotificationService,
    private zone: NgZone,
  ) {
    if (!firebase.default.apps.length) {
      firebase.default.initializeApp(environment.firebaseConfig);
    }
  }

  subscribeToFirebase() {
    this.angularFireMessaging.messages.subscribe((message) => {
      this.onMessage(message);
    });
    this.angularFireMessaging.tokenChanges.subscribe((token) => {
      console.warn('Firebase token refreshed:', token);
      this.setFirebaseToken(token);
    });
    this.subscribeToBroadcastChannel();
  }

  subscribeToBroadcastChannel() {
    if (firebase.default.messaging.isSupported()) {
      const channel = new BroadcastChannel('sw-messages');
      channel.addEventListener('message', (message) => this.onMessage(message.data));
      console.log('firebase service init');
    } else {
      console.log('BroadcastChannel not supported');
    }
  }

  public setFirebaseToken(token: string) {
    console.log('Setting Firebase token:', token);
    if (!token) {
      throw new Error(`Firebase Token is Required`);
    }
    localStorage.setItem(StorageTypeKey.FIREBASE_TOKEN, token);
  }

  async getFirebaseToken(): Promise<string | null> {
    console.log('Getting Firebase token from localStorage');
    const token = localStorage.getItem(StorageTypeKey.FIREBASE_TOKEN);
    if (token) {
      return token;
    }
    // If token does not exist, request permission and get a new token
    return await this.requestPermission();
  }

  onMessage(message: any) {
    const notificationData = message.data?.notification || message.notification;

    if (!notificationData) {
      console.log('No notification data found in message:', message);
      return;
    }

    let notification: UserNotification | AppNotification;
    if (typeof notificationData === 'string') {
      notification = JSON.parse(notificationData);
    } else {
      notification = notificationData;
    }

    console.log('in firebase we got a message', notification);
    this.notificationService.addNewNotification(notification);
    this.zone.run(() => {
      this.getObservable(notification.topic)?.next(notification as UserNotification);
      this.getObservable(notification.topic)?.next(null);
    });
  }

  async requestPermission(): Promise<string | null> {
    console.log('Requesting permission for notifications...');

    //Early return if previously denied
    if (window.Notification.permission === 'denied') {
      console.log('Notification permission previously denied');
      return null;
    }

    //Request permission if not already granted
    if (window.Notification.permission !== 'granted') {
      const permission = await window.Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission not granted:', permission);
        return null;
      }
    }

    const messaging = firebase.default.messaging();
    const token = await messaging.getToken({
      vapidKey: environment.firebaseVapidKey,
    });

    if (!token) {
      throw new Error('Failed to obtain Firebase token');
    }

    this.setFirebaseToken(token);
    this.subscribeToFirebase();
    return token;
  }
}
